import { Script, Workflow, Execution, Notification } from '@/types';
import { MOCK_SCRIPTS, MOCK_WORKFLOWS } from './mock-data';
import { showErrorToast } from './error-toast';
import { recordBackendError, noteRequestId } from './client-logger';
import { getToken, clearTokenCache } from './authToken';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';

async function getAuthToken(): Promise<string | null> {
  return getToken();
}

// Clear module-level token cache. Must be called on signOut so the next
// user's tab on the same browser doesn't briefly carry the previous user's
// Bearer token into the first authenticated request. Routes to the unified
// cache in authToken.ts; kept under this name for backwards compatibility.
export function clearApiAuthCache() {
  clearTokenCache();
}

// Routes that legitimately return non-2xx during normal use; suppress
// the global toast for these so the user doesn't see "Code: CRSC-XXXX"
// for every guest poll.
const TOAST_SUPPRESS_PATTERNS: RegExp[] = [
  /\/api\/notifications($|\?)/, // bell poll on logged-out tab
  /\/api\/connection\/status/,  // probed before login
  /\/api\/profile($|\?)/,       // probed pre-auth on some routes
];

function shouldSuppressToast(path: string): boolean {
  return TOAST_SUPPRESS_PATTERNS.some((re) => re.test(path));
}

async function readErrorEnvelope(res: Response): Promise<{ code?: string; error_id?: string; category?: string; message_for_operator?: string }> {
  try {
    const cloned = res.clone();
    const body = await cloned.json().catch(() => null);
    if (body && typeof body === 'object' && body.error && typeof body.error === 'object') {
      return body.error as { code?: string; error_id?: string; category?: string; message_for_operator?: string };
    }
  } catch { /* fall through */ }
  return {};
}

async function fetchApi<T>(path: string, opts?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const authHeader: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...opts,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...authHeader, ...opts?.headers },
    });
    // Always note the response request_id, even on 2xx, so a later
    // bug report has the latest id pinned.
    const requestId = res.headers.get('x-request-id');
    if (requestId) noteRequestId(requestId);
    if (!res.ok) {
      const envelope = await readErrorEnvelope(res);
      // Record + toast (toast suppressed for known noisy polling paths).
      recordBackendError({
        ts: new Date().toISOString(),
        status: res.status,
        request_id: requestId || envelope.error_id || undefined,
        error_code: envelope.code,
        error_id: envelope.error_id,
        category: envelope.category,
        url: path,
        message_for_operator: envelope.message_for_operator,
      });
      if (!shouldSuppressToast(path)) {
        showErrorToast({
          code: envelope.code,
          error_id: envelope.error_id,
          category: envelope.category,
          message_for_operator: envelope.message_for_operator,
          request_id: requestId,
          url: path,
          status: res.status,
        });
      }
      throw new Error(`API error: ${res.status}`);
    }
    return res.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

type AnyRecord = Record<string, unknown>;

function asRecord(value: unknown): AnyRecord | null {
  return value && typeof value === 'object' ? (value as AnyRecord) : null;
}

function unwrapArray<T>(value: unknown, keys: string[] = []): T[] {
  if (Array.isArray(value)) return value as T[];
  const record = asRecord(value);
  if (!record) return [];
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as T[];
  }
  if (Array.isArray(record.data)) return record.data as T[];
  const nestedData = asRecord(record.data);
  if (nestedData) {
    for (const key of keys) {
      if (Array.isArray(nestedData[key])) return nestedData[key] as T[];
    }
  }
  return [];
}

function unwrapObject<T>(value: unknown, keys: string[] = []): T | null {
  const record = asRecord(value);
  if (!record) return null;
  for (const key of keys) {
    const candidate = asRecord(record[key]);
    if (candidate) return candidate as T;
  }
  const nestedData = asRecord(record.data);
  if (nestedData) {
    for (const key of keys) {
      const candidate = asRecord(nestedData[key]);
      if (candidate) return candidate as T;
    }
  }
  return record as T;
}

function normalizeWorkflow(workflow: Partial<Workflow>): Workflow {
  const nodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
  // Infer trigger_type from trigger node data if not set at workflow level
  let triggerType = workflow.trigger_type;
  if (!triggerType) {
    const triggerNode = nodes.find(n => n.type === 'trigger');
    triggerType = (triggerNode?.data?.triggerType as Workflow['trigger_type']) || 'manual';
  }
  return {
    id: workflow.id || `wf-${Date.now()}`,
    name: workflow.name || 'Untitled Workflow',
    description: workflow.description || '',
    trigger_type: triggerType,
    trigger_config: workflow.trigger_config || {},
    nodes,
    edges: Array.isArray(workflow.edges) ? workflow.edges : [],
    is_active: Boolean(workflow.is_active),
    package_id: workflow.package_id,
    settings: workflow.settings || { smartRouting: true },
    folder_id: workflow.folder_id,
    icon: workflow.icon,
    customIcon: workflow.customIcon,
    created_at: workflow.created_at || new Date().toISOString(),
    updated_at: workflow.updated_at || workflow.created_at || new Date().toISOString(),
    last_run: workflow.last_run,
  };
}

// ── Scripts ───────────────────────────────────────────────────────────────────

export async function getScripts(): Promise<Script[]> {
  try {
    const payload = await fetchApi<unknown>('/api/scripts');
    return unwrapArray<Script>(payload, ['scripts']).map((script) => ({
      ...script,
      inputs: Array.isArray(script.inputs) ? script.inputs : [],
      outputs: Array.isArray(script.outputs) ? script.outputs : [],
    }));
  }
  catch { return []; }
}

export async function getScript(id: string): Promise<Script> {
  return fetchApi<Script>(`/api/scripts/${id}`);
}

export async function toggleScript(id: string, is_active: boolean): Promise<Script> {
  try {
    return await fetchApi<Script>(`/api/scripts/${id}`, {
      method: 'PATCH', body: JSON.stringify({ is_active }),
    });
  } catch {
    const s = MOCK_SCRIPTS.find(s => s.id === id);
    if (s) s.is_active = is_active;
    return s || MOCK_SCRIPTS[0];
  }
}

// ── Workflows ─────────────────────────────────────────────────────────────────

export async function getWorkflows(): Promise<Workflow[]> {
  try {
    const payload = await fetchApi<unknown>('/api/workflows');
    const workflows = unwrapArray<Partial<Workflow>>(payload, ['workflows']);
    return workflows.map(normalizeWorkflow);
  }
  catch { return []; }
}

export async function getWorkflow(id: string): Promise<Workflow> {
  const payload = await fetchApi<unknown>(`/api/workflows/${id}`);
  const workflow = unwrapObject<Partial<Workflow>>(payload, ['workflow']);
  if (!workflow) throw new Error('Invalid workflow payload');
  return normalizeWorkflow(workflow);
}

export async function createWorkflow(data: Partial<Workflow>): Promise<Workflow> {
  const payload = await fetchApi<unknown>('/api/workflows', {
    method: 'POST', body: JSON.stringify(data),
  });
  const workflow = unwrapObject<Partial<Workflow>>(payload, ['workflow']);
  if (!workflow || !workflow.id) {
    throw new Error('Create workflow failed: server did not return a workflow id');
  }
  return normalizeWorkflow(workflow);
}

export async function updateWorkflow(id: string, data: Partial<Workflow>): Promise<Workflow> {
  try {
    const payload = await fetchApi<unknown>(`/api/workflows/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
    const workflow = unwrapObject<Partial<Workflow>>(payload, ['workflow']);
    if (!workflow) throw new Error('Invalid update workflow payload');
    return normalizeWorkflow(workflow);
  } catch {
    const idx = MOCK_WORKFLOWS.findIndex(w => w.id === id);
    if (idx >= 0) {
      MOCK_WORKFLOWS[idx] = { ...MOCK_WORKFLOWS[idx], ...data, updated_at: new Date().toISOString() };
      return MOCK_WORKFLOWS[idx];
    }
    return MOCK_WORKFLOWS[0];
  }
}

export async function toggleWorkflow(id: string, is_active: boolean): Promise<Workflow> {
  try {
    const payload = await fetchApi<unknown>(`/api/workflows/${id}/toggle`, {
      method: 'PATCH', body: JSON.stringify({ is_active }),
    });
    const workflow = unwrapObject<Partial<Workflow>>(payload, ['workflow']);
    if (!workflow) throw new Error('Invalid toggle workflow payload');
    return normalizeWorkflow(workflow);
  } catch {
    const w = MOCK_WORKFLOWS.find(w => w.id === id);
    if (w) w.is_active = is_active;
    return w || MOCK_WORKFLOWS[0];
  }
}

export async function deleteWorkflow(id: string): Promise<void> {
  try { await fetchApi(`/api/workflows/${id}`, { method: 'DELETE' }); }
  catch {
    const idx = MOCK_WORKFLOWS.findIndex(w => w.id === id);
    if (idx >= 0) MOCK_WORKFLOWS.splice(idx, 1);
  }
}

export async function executeWorkflow(id: string, triggerData?: Record<string, unknown>, isTest = true): Promise<{ status: string; workflowId: string; executionId?: string }> {
  try {
    return await fetchApi<{ status: string; workflowId: string; executionId?: string }>(`/api/workflows/${id}/execute`, {
      method: 'POST', body: JSON.stringify({ triggerData: triggerData || {}, isTest }),
    });
  } catch {
    return { status: 'started', workflowId: id };
  }
}

export async function liveTestWorkflow(id: string): Promise<{ status: string; executionId?: string; testCustomerId?: string; error?: string }> {
  return fetchApi(`/api/workflows/${id}/live-test`, { method: 'POST' });
}

// ── Executions ────────────────────────────────────────────────────────────────

export async function getExecutions(status?: string): Promise<Execution[]> {
  try {
    const qs = status ? `?status=${status}` : '';
    return await fetchApi<Execution[]>(`/api/executions${qs}`);
  } catch { return []; }
}

export async function getExecution(id: string): Promise<Execution> {
  return fetchApi<Execution>(`/api/executions/${id}`);
}

export async function resumeExecution(id: string, inputData: Record<string, unknown>): Promise<unknown> {
  return fetchApi(`/api/executions/${id}/resume`, {
    method: 'POST', body: JSON.stringify(inputData),
  });
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function getNotifications(): Promise<Notification[]> {
  try { return await fetchApi<Notification[]>('/api/notifications'); }
  catch { return []; }
}

export async function markNotificationRead(id: string): Promise<void> {
  try { await fetchApi(`/api/notifications/${id}/read`, { method: 'POST' }); }
  catch { /* silent */ }
}

export async function markAllNotificationsRead(): Promise<void> {
  try { await fetchApi('/api/notifications/read-all', { method: 'POST' }); }
  catch { /* silent */ }
}

// ── CRM Connection ────────────────────────────────────────────────────────────

export interface ConnectionStatus {
  connected: boolean;
  cookies_present: boolean;
  connectedAt: string | null;
  lastSync: string | null;
  lastChecked?: string | null;
  error?: string;
}

export interface SyncResult {
  status: string;
  syncedAt: string;
  counts: { customers: number; templates: number; services: number; visits: number };
  errors?: Record<string, string | null>;
}

export interface SyncStatus {
  syncedAt: string | null;
  counts: { customers: number; templates: number; services: number; visits: number };
}

export async function getConnectionStatus(): Promise<ConnectionStatus> {
  try { return await fetchApi<ConnectionStatus>('/api/connection/status-v2'); }
  catch { return { connected: false, cookies_present: false, connectedAt: null, lastSync: null }; }
}

export async function getSyncStatus(): Promise<SyncStatus> {
  try { return await fetchApi<SyncStatus>('/api/sync/status'); }
  catch { return { syncedAt: null, counts: { customers: 0, templates: 0, services: 0, visits: 0 } }; }
}

export async function triggerSync(): Promise<SyncResult> {
  try { return await fetchApi<SyncResult>('/api/sync', { method: 'POST' }); }
  catch {
    return { status: 'error', syncedAt: new Date().toISOString(), counts: { customers: 0, templates: 0, services: 0, visits: 0 } };
  }
}

export async function getCrmTemplates(): Promise<{ templates: Array<{ id: string; name: string }>; syncedAt: string | null }> {
  try { return await fetchApi('/api/crm/templates'); }
  catch { return { templates: [], syncedAt: null }; }
}

export async function getCrmServices(): Promise<{ services: Array<{ id: string; name: string; defaultCost: number }>; syncedAt: string | null }> {
  try { return await fetchApi('/api/crm/services'); }
  catch { return { services: [], syncedAt: null }; }
}

export async function getCrmCustomers(): Promise<{ customers: Array<{ id: string; name: string; email: string }>; syncedAt: string | null }> {
  try { return await fetchApi('/api/crm/customers'); }
  catch { return { customers: [], syncedAt: null }; }
}

// ── Packages ──────────────────────────────────────────────────────────────────

export interface PackageStatus { id: string; is_active: boolean; workflowId?: string; }

export async function getPackages(): Promise<PackageStatus[]> {
  try { return await fetchApi<PackageStatus[]>('/api/packages'); }
  catch { return []; }
}

export async function activatePackage(id: string): Promise<{ workflowId: string }> {
  return fetchApi<{ workflowId: string }>(`/api/packages/${id}/activate`, { method: 'POST' });
}

export async function deactivatePackage(id: string): Promise<void> {
  await fetchApi(`/api/packages/${id}/deactivate`, { method: 'POST' });
}

// ── Profile ───────────────────────────────────────────────────────────────────

export interface UserProfile { name: string; email: string; company: string; role: string; postal_code?: string; includeTestsInReports?: boolean; }

export async function getProfile(): Promise<UserProfile> {
  try { return await fetchApi<UserProfile>('/api/profile'); }
  catch { return { name: '', email: '', company: 'CrewSync', role: 'owner', postal_code: '' }; }
}

export async function updateProfile(profile: UserProfile): Promise<{ status: string; profile: UserProfile }> {
  try {
    return await fetchApi('/api/profile', { method: 'POST', body: JSON.stringify(profile) });
  } catch { return { status: 'error', profile }; }
}

// ── Reports ───────────────────────────────────────────────────────────────────

export interface ReportSummary {
  totalExecutions: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  avgDuration: number | null;
  fastestWorkflow: { name: string; avgDurationMs: number } | null;
  previousPeriodSuccessRate: number | null;
  topWorkflows: { name: string; runs: number; successRate: number; avgDurationMs: number | null }[];
  daily: { date: string; success: number; failed: number }[];
  executionsByDay: { date: string; success: number; failed: number }[];
  timeSaved: number | null;
  executions: import('@/types').Execution[];
}

export async function getReportSummary(start: string, end: string, includeTests = true): Promise<ReportSummary> {
  try {
    return await fetchApi<ReportSummary>(`/api/reports/summary?start=${start}&end=${end}&includeTests=${includeTests}`);
  } catch {
    return {
      totalExecutions: 0, successCount: 0, failedCount: 0, successRate: 0,
      avgDuration: null, fastestWorkflow: null, previousPeriodSuccessRate: null,
      topWorkflows: [], daily: [], executionsByDay: [], timeSaved: null, executions: [],
    };
  }
}

// ── Task Durations ────────────────────────────────────────────────────────────

export interface TaskDurations {
  estimate: number; followup: number; schedule: number;
  cancellation: number; invoice: number; onboard: number;
}

export async function getTaskDurations(): Promise<TaskDurations | null> {
  try {
    const p = await fetchApi<{ taskDurations?: TaskDurations }>('/api/profile');
    return p.taskDurations || null;
  } catch { return null; }
}

export async function saveTaskDurations(durations: TaskDurations): Promise<void> {
  try {
    await fetchApi('/api/profile/task-durations', { method: 'POST', body: JSON.stringify(durations) });
  } catch { /* silent */ }
}

export async function reconnectCrm(): Promise<{ status: string; message?: string }> {
  return fetchApi('/api/connection/reconnect', { method: 'POST' });
}

export interface CustomerRecord {
  id: string;
  copilotId: string | null;
  name: string;
  email: string;
  phone: string;
  address: string;
  firstSeen: string;
  executionCount: number;
  lastExecution: { id: string; status: string; workflow: string; at: string } | null;
  executions: string[];
}

export async function getCustomers(): Promise<{ customers: CustomerRecord[] }> {
  return fetchApi('/api/executions/customers');
}

// ── Simulation ────────────────────────────────────────────────────────────────

export interface SimulationConfig {
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  services?: string;
  lawnSqft?: string;
  commMode?: 'silent' | 'redirect' | 'live';
  delayMultiplier?: number;
  maxScenarios?: number;
  maxServicesPerScenario?: number;
  minSqft?: number;
  maxSqft?: number;
  employees?: string[];
  scheduleDate?: string;
}

export interface SimulationSession {
  id: string;
  workflowId: string;
  workflowName: string;
  status: 'setup' | 'running' | 'completed' | 'cleaning' | 'cleaned';
  config: SimulationConfig;
  scenarios: SimScenario[];
  createdResources: { type: string; id: string; scenarioId: string; label: string }[];
  startedAt: string | null;
  completedAt: string | null;
}

export interface SimScenario {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  steps: { nodeId: string; nodeName: string; status: string; error: string | null; duration: number; intercepted: boolean }[];
  edgeIds?: string[];
  color?: string;
  colorName?: string;
  customerId: string | null;
  error: string | null;
}

export interface SimAnalysis {
  pathCount: number;
  description: string;
  scenarioCount: number;
  scenarios: { id: string; label: string; stepCount: number }[];
}

export async function createSimulation(workflowId: string, config: SimulationConfig): Promise<SimulationSession> {
  return fetchApi('/api/simulation/create', { method: 'POST', body: JSON.stringify({ workflowId, config }) });
}

export async function getSimulation(id: string): Promise<SimulationSession> {
  return fetchApi(`/api/simulation/${id}`);
}

export async function analyzeSimulation(id: string): Promise<SimAnalysis> {
  return fetchApi(`/api/simulation/${id}/analyze`, { method: 'POST' });
}

export async function startSimulation(id: string): Promise<{ status: string; sessionId: string; totalNodes: number }> {
  return fetchApi(`/api/simulation/${id}/run`, { method: 'POST' });
}

export async function rerunSimulation(id: string, mode: 'all' | 'failed'): Promise<{ status: string }> {
  return fetchApi(`/api/simulation/${id}/rerun`, { method: 'POST', body: JSON.stringify({ mode }) });
}

export async function cleanupSimulation(id: string): Promise<{ status: string }> {
  return fetchApi(`/api/simulation/${id}/cleanup`, { method: 'POST' });
}

export async function deleteSimulation(id: string): Promise<void> {
  await fetchApi(`/api/simulation/${id}`, { method: 'DELETE' });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function connectSimulationFeed(id: string, onEvent: (event: any) => void) : EventSource {
  const url = `${API_URL}/api/simulation/${id}/feed`;
  const es = new EventSource(url);
  es.onmessage = (e) => {
    try { onEvent(JSON.parse(e.data)); } catch {}
  };
    es.addEventListener('message', (e) => {
    try {
      const evt = JSON.parse(e.data);
      if (evt.type === 'simulation_complete') {
        es.close();
      }
    } catch {}
  });
  return es;
}
// ── Quick Test Simulation API ───────────────────────────────────────────────

export interface SimPollResponse {
  status: string;
  feed: { type: string; nodeId: string | null; nodeName: string | null; status: string; timestamp: string; output?: unknown; error?: string | null; duration?: number }[];
  currentNode: string | null;
  resources: { type: string; id: string; label: string; createdAt?: string }[];
  waitingFor: { nodeId: string; prompt: string; inputType: string; options: string[]; waitType: string; waitFor: string; employees: string[] } | null;
  executionId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

export async function triggerSimulation(id: string, triggerData: Record<string, unknown>, mode: string = 'test'): Promise<{ status: string; sessionId: string; executionId: string }> {
  return fetchApi(`/api/simulation/${id}/trigger`, { method: 'POST', body: JSON.stringify({ triggerData, mode }) });
}

export async function pollSimulation(id: string): Promise<SimPollResponse> {
  return fetchApi(`/api/simulation/${id}/poll`);
}

export async function submitSimulationInput(id: string, nodeId: string, value: Record<string, unknown>): Promise<{ status: string }> {
  return fetchApi(`/api/simulation/${id}/input`, { method: 'POST', body: JSON.stringify({ nodeId, value }) });
}

export async function searchCustomers(q: string): Promise<{ id: string; name: string; tag: string; address: string; email: string; phone: string; street: string; city: string; state: string; zip: string }[]> {
  const res = await fetchApi<{ data: { id: string; name: string; tag: string; address: string; email: string; phone: string; street: string; city: string; state: string; zip: string }[] }>(`/api/scripts/crm-data/customers/fast?q=${encodeURIComponent(q)}`);
  return res.data || [];
}


// ── Folders ───────────────────────────────────────────────────────────────────

export interface Folder {
  id: string;
  name: string;
  color: string;
  emoji: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export async function fetchFolders(): Promise<Folder[]> {
  try {
    const payload = await fetchApi<unknown>('/api/folders');
    return unwrapArray<Folder>(payload, ['folders']);
  } catch { return []; }
}

export async function createFolder(data: { name: string; color?: string; emoji?: string; description?: string }): Promise<Folder> {
  const payload = await fetchApi<{ folder: Folder }>('/api/folders', {
    method: 'POST', body: JSON.stringify(data),
  });
  return payload.folder;
}

export async function updateFolder(id: string, data: Partial<Folder>): Promise<Folder> {
  const payload = await fetchApi<{ folder: Folder }>(`/api/folders/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  });
  return payload.folder;
}

export async function deleteFolder(id: string): Promise<void> {
  await fetchApi(`/api/folders/${id}`, { method: 'DELETE' });
}

// ── Workflow Packages ─────────────────────────────────────────────────────────

export interface WorkflowPackage {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  tags: string[];
  category?: string;
  popular?: boolean;
  steps?: number;
}

export async function getWorkflowPackages(): Promise<WorkflowPackage[]> {
  try {
    const payload = await fetchApi<unknown>('/api/workflow-packages');
    return unwrapArray<WorkflowPackage>(payload, ['packages']);
  } catch { return []; }
}

export async function installWorkflowPackage(id: string, name?: string): Promise<Workflow> {
  const payload = await fetchApi<unknown>(`/api/workflow-packages/${id}/install`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  const workflow = unwrapObject<Partial<Workflow>>(payload, ['workflow']);
  if (!workflow) throw new Error('Install failed');
  return normalizeWorkflow(workflow);
}

// ── Composites ────────────────────────────────────────────────────────────────

export interface CompositeDefinition {
  id: string;
  name: string;
  category: string;
  description?: string;
  subSteps?: Array<{
    id: string;
    label: string;
    alwaysOn?: boolean;
    enabled: boolean;
    configFields?: Array<{
      key: string;
      label: string;
      type: 'text' | 'select' | 'tags-add' | 'tags-remove' | 'number' | 'crm-templates' | 'crm-sms-templates' | 'crm-employees' | 'crm-crews' | 'toggle-passthrough' | 'hold' | 'recipient' | 'multi-send' | 'comm-mode';
      options?: string[];
      value: string | string[] | Record<string, unknown>;
      channelType?: string;
      description?: string;
    }>;
  }>;
}

export async function fetchComposites(): Promise<CompositeDefinition[]> {
  try {
    const payload = await fetchApi<unknown>('/api/composites');
    const composites = unwrapArray<CompositeDefinition>(payload, ['composites']);
    return composites.length > 0 ? composites : BUILTIN_COMPOSITES;
  } catch {
    // Return built-in composite definitions as fallback
    return BUILTIN_COMPOSITES;
  }
}

export async function fetchComposite(id: string): Promise<CompositeDefinition> {
  try {
    const payload = await fetchApi<unknown>(`/api/composites/${id}`);
    const composite = unwrapObject<CompositeDefinition>(payload, ['composite']);
    if (composite) return composite;
    throw new Error('Invalid composite payload');
  } catch {
    return BUILTIN_COMPOSITES.find(c => c.id === id) || BUILTIN_COMPOSITES[0];
  }
}

export async function fetchAvailableTags(): Promise<string[]> {
  try {
    const payload = await fetchApi<unknown>('/api/scripts/crm-data/tags');
    const rawTags = unwrapArray<string | { id?: string; name?: string; label?: string }>(payload, ['tags']);
    return rawTags
      .map((item) => {
        if (typeof item === 'string') return item;
        return item.name || item.label || item.id || '';
      })
      .filter((tag): tag is string => Boolean(tag));
  } catch {
    return [];
  }
}

export async function fetchTemplates(): Promise<Array<{ id: string; name: string }>> {
  try {
    const payload = await fetchApi<{ data: Array<{ id: string; name: string }> }>('/api/scripts/crm-data/templates');
    return payload.data || [];
  } catch {
    return [];
  }
}

export async function fetchEmployees(): Promise<Array<{ id: string; name: string }>> {
  try {
    const payload = await fetchApi<{ data: Array<{ id: string; name: string }> }>('/api/scripts/crm-data/employees');
    return payload.data || [];
  } catch {
    return [];
  }
}

export async function fetchSmsTemplates(): Promise<Array<{ id: string; name: string }>> {
  try {
    const payload = await fetchApi<{ data: Array<{ id: string; name: string }> }>('/api/scripts/crm-data/sms-templates');
    return payload.data || [];
  } catch {
    return [];
  }
}

export async function fetchCrews(): Promise<Array<{ id: string; name: string }>> {
  try {
    const payload = await fetchApi<{ data: Array<{ id: string; name: string }> }>('/api/scripts/crm-data/crews');
    return payload.data || [];
  } catch {
    return [];
  }
}

export async function fetchCompositeTagContext(
  compositeId: string,
  nodeId: string,
  workflowContext?: { nodes: unknown[]; edges: unknown[] }
): Promise<string[]> {
  try {
    const params = new URLSearchParams({ nodeId });
    if (workflowContext) params.set('workflow', JSON.stringify(workflowContext));
    const payload = await fetchApi<unknown>(`/api/composites/${compositeId}/tags?${params}`);
    const directTags = unwrapArray<string>(payload, ['tags']);
    if (directTags.length > 0) return directTags;

    const record = asRecord(payload);
    const tagsRecord = record ? asRecord(record.tags) : null;
    if (tagsRecord && Array.isArray(tagsRecord.available)) {
      return (tagsRecord.available as unknown[])
        .filter((t): t is string => typeof t === 'string')
        .filter(Boolean);
    }
    return [];
  } catch {
    return [];
  }
}

// Built-in composite fallbacks (mirrors COMPOSITE-NODES-SPEC.md)
const BUILTIN_COMPOSITES: CompositeDefinition[] = [
  {
    id: 'onboard-customer',
    name: 'Onboard Customer',
    category: 'Customers',
    description: 'Create customer + property',
    subSteps: [
      { id: 'create-customer', label: 'Create Customer', alwaysOn: true, enabled: true },
      { id: 'create-property', label: 'Create Property', alwaysOn: true, enabled: true },
      { id: 'add-tags', label: 'Add Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags to Add', type: 'tags-add', value: [] }] },
      { id: 'update-notes', label: 'Update Notes', enabled: false, configFields: [{ key: 'notes', label: 'Notes', type: 'text', value: '' }] },
      { id: 'send-welcome-email', label: 'Send Welcome Email', enabled: false, configFields: [{ key: 'templateId', label: 'Template ID', type: 'text', value: '' }] },
    ],
  },
  {
    id: 'create-estimate',
    name: 'Create Estimate',
    category: 'Estimates',
    description: 'Price + create + send estimate',
    subSteps: [
      { id: 'pricing', label: 'Auto Pricing (rate matrix)', alwaysOn: true, enabled: true },
      { id: 'create-est', label: 'Create Estimate', alwaysOn: true, enabled: true },
      { id: 'send-mode', label: 'Send Mode', enabled: true, configFields: [{ key: 'sendMode', label: 'Send Behavior', type: 'select', options: ['Send Immediately', 'Send After Delay', 'Hold for Approval'], value: 'Send Immediately' }] },
      { id: 'add-tags', label: 'Add Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-add', value: [] }] },
      { id: 'remove-tags', label: 'Remove Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-remove', value: [] }] },
    ],
  },
  {
    id: 'estimate-response',
    name: 'Estimate Response',
    category: 'Estimates',
    description: 'Accept or decline estimate',
    subSteps: [
      { id: 'accept-path', label: 'Accept Estimate', alwaysOn: true, enabled: true },
      { id: 'convert-invoice', label: 'Convert to Invoice', enabled: true },
      { id: 'add-tags-accept', label: 'Add Tags (Accept)', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-add', value: [] }] },
      { id: 'decline-path', label: 'Decline: Archive/Delete', enabled: false, configFields: [{ key: 'action', label: 'Action', type: 'select', options: ['Archive', 'Delete'], value: 'Archive' }] },
    ],
  },
  {
    id: 'schedule-visit',
    name: 'Schedule Visit',
    category: 'Visits',
    description: 'Create + assign + notify',
    subSteps: [
      { id: 'create-visit', label: 'Create Visit', alwaysOn: true, enabled: true, configFields: [{ key: 'frequency', label: 'Frequency', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly'], value: 'Weekly' }] },
      { id: 'assign-crew', label: 'Assign Crew', enabled: false, configFields: [{ key: 'employee', label: 'Employee', type: 'text', value: '' }] },
      { id: 'send-confirmation', label: 'Send Confirmation Email/SMS', enabled: true },
      { id: 'notify-office', label: 'Notify Office', enabled: false },
      { id: 'add-tags', label: 'Add Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-add', value: [] }] },
    ],
  },
  {
    id: 'complete-visit',
    name: 'Complete Visit',
    category: 'Visits',
    description: 'Invoice + photos + followup',
    subSteps: [
      { id: 'invoice-visit', label: 'Invoice Visit', alwaysOn: true, enabled: true },
      { id: 'upload-photos', label: 'Upload Visit Photos', enabled: false },
      { id: 'followup-email', label: 'Send Follow-up Email/SMS', enabled: true },
      { id: 'notify-office', label: 'Notify Office', enabled: false },
      { id: 'add-tags', label: 'Add Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-add', value: [] }] },
    ],
  },
  {
    id: 'send-communication',
    name: 'Send Communication',
    category: 'Email/SMS',
    description: 'Email, SMS, or both',
    subSteps: [
      { id: 'channel', label: 'Channel', alwaysOn: true, enabled: true, configFields: [{ key: 'channel', label: 'Channel', type: 'select', options: ['Email', 'SMS', 'Both'], value: 'Email' }] },
      { id: 'add-tags', label: 'Add Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags', type: 'tags-add', value: [] }] },
    ],
  },
  {
    id: 'manage-tags',
    name: 'Manage Tags',
    category: 'Tags',
    description: 'Add and remove tags',
    subSteps: [
      { id: 'add-tags', label: 'Add Tags', enabled: true, configFields: [{ key: 'tags', label: 'Tags to Add', type: 'tags-add', value: [] }] },
      { id: 'remove-tags', label: 'Remove Tags', enabled: false, configFields: [{ key: 'tags', label: 'Tags to Remove', type: 'tags-remove', value: [] }] },
    ],
  },
  {
    id: 'manage-property',
    name: 'Manage Property',
    category: 'Properties',
    description: 'Update, upload photos/docs',
    subSteps: [
      { id: 'update-property', label: 'Update Property', enabled: true },
      { id: 'upload-photos', label: 'Upload Photos', enabled: false },
      { id: 'upload-docs', label: 'Upload Documents', enabled: false },
      { id: 'transfer-photos', label: 'Transfer Photos', enabled: false },
    ],
  },
];

// ── CRM Data Cache ────────────────────────────────────────────────────────────
// Fetched once per page load, all dropdowns read from cache instantly.

interface CrmCache {
  tags: string[];
  templates: Array<{ id: string; name: string }>;
  smsTemplates: Array<{ id: string; name: string }>;
  employees: Array<{ id: string; name: string }>;
  crews: Array<{ id: string; name: string }>;
  loaded: boolean;
  loading: Promise<void> | null;
}

const crmCache: CrmCache = {
  tags: [],
  templates: [],
  smsTemplates: [],
  employees: [],
  crews: [],
  loaded: false,
  loading: null
};

/** Prefetch all CRM data (tags, etc.) — call once when workflow editor mounts. */
export function preloadCrmData(): Promise<void> {
  if (crmCache.loaded) return Promise.resolve();
  if (crmCache.loading) return crmCache.loading;
  crmCache.loading = (async () => {
    try {
      // Fetch all CRM data in parallel
      const [tags, templates, smsTemplates, employees, crews] = await Promise.all([
        fetchAvailableTags(),
        fetchTemplates(),
        fetchSmsTemplates(),
        fetchEmployees(),
        fetchCrews()
      ]);
      crmCache.tags = tags;
      crmCache.templates = templates;
      crmCache.smsTemplates = smsTemplates;
      crmCache.employees = employees;
      crmCache.crews = crews;
    } catch {
      // Keep existing values on error
    }
    crmCache.loaded = true;
    crmCache.loading = null;
  })();
  return crmCache.loading;
}

/** Get cached tags instantly. Returns empty array if not yet loaded. */
export function getCachedTags(): string[] {
  return crmCache.tags;
}

/** Get cached templates instantly. Returns empty array if not yet loaded. */
export function getCachedTemplates(): Array<{ id: string; name: string }> {
  return crmCache.templates;
}

/** Get cached employees instantly. Returns empty array if not yet loaded. */
export function getCachedEmployees(): Array<{ id: string; name: string }> {
  return crmCache.employees;
}

/** Get cached SMS templates instantly. Returns empty array if not yet loaded. */
export function getCachedSmsTemplates(): Array<{ id: string; name: string }> {
  return crmCache.smsTemplates;
}

/** Get cached crews instantly. Returns empty array if not yet loaded. */
export function getCachedCrews(): Array<{ id: string; name: string }> {
  return crmCache.crews;
}

/** Check if CRM data is preloaded. */
export function isCrmDataLoaded(): boolean {
  return crmCache.loaded;
}

/** Force refresh the cache (e.g., on manual page refresh). */
export function invalidateCrmCache(): void {
  crmCache.loaded = false;
  crmCache.loading = null;
  crmCache.tags = [];
  crmCache.templates = [];
  crmCache.smsTemplates = [];
  crmCache.employees = [];
  crmCache.crews = [];
}

export async function addSimulationTag(cid: string): Promise<{ status: string }> {
  return fetchApi<{ status: string }>(`/api/scripts/crm-data/customers/${cid}/simulation-tag`, { method: 'POST' });
}

export async function fetchPropertySqft(cid: string): Promise<{ sqft: string; propertyId: string; cid: string }> {
  return fetchApi<{ sqft: string; propertyId: string; cid: string }>(`/api/scripts/crm-data/customers/${cid}/property`);
}

// ── Email Templates ───────────────────────────────────────────────────────────

export async function fetchEmailTemplates(): Promise<{ templates: Array<{ id: string; name: string; subject?: string; html?: string }>; count: number }> {
  return fetchApi<{ templates: Array<{ id: string; name: string; subject?: string; html?: string }>; count: number }>('/api/email-templates');
}

export async function fetchEmailTemplate(id: string): Promise<{ template: { id: string; name: string; subject: string; html: string; content: string; deletable?: boolean } }> {
  return fetchApi<{ template: { id: string; name: string; subject: string; html: string; content: string; deletable?: boolean } }>(`/api/email-templates/${id}`);
}

export async function updateEmailTemplate(id: string, data: { name: string; subject: string; html: string; content: string }): Promise<{ success: boolean; id: string }> {
  return fetchApi<{ success: boolean; id: string }>(`/api/email-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function chatEmailBuilder(messages: Array<{ role: string; content: string }>): Promise<{ content: string; htmlBlocks: Array<{ index: number; name: string; html: string }>; assistantMessage: { role: string; content: string } }> {
  return fetchApi<{ content: string; htmlBlocks: Array<{ index: number; name: string; html: string }>; assistantMessage: { role: string; content: string } }>('/api/email-templates/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
}
